import { z } from 'zod';

/**
 * Articles a site gets written and published (ADR-0051): topics proposed from
 * what the site is about and what people search for, each article researched
 * on the web and written in the site's language, then published to its CMS —
 * after a person has read it, as a draft in the CMS, or straight away.
 */

/**
 * Where an article stands:
 * idea → queued → writing → ready → publishing → published;
 * failed at any step with the reason; rejected by a person.
 */
export const CONTENT_STATES = ['idea', 'queued', 'writing', 'ready', 'publishing', 'published', 'failed', 'rejected'] as const;
export type ContentState = (typeof CONTENT_STATES)[number];

/** The two halves of writing an article (ADR-0051): the material first, then the text. */
export const CONTENT_STAGES = ['research', 'writing'] as const;
export type ContentStage = (typeof CONTENT_STAGES)[number];

/**
 * What happens to a written article:
 * - `review` — it waits in the panel for a person to publish it;
 * - `wp_draft` — it goes to the CMS as a draft, for the editor there;
 * - `publish` — it is published, spaced over the week.
 */
export const CONTENT_MODES = ['review', 'wp_draft', 'publish'] as const;
export type ContentMode = (typeof CONTENT_MODES)[number];

export type ContentAction = 'write' | 'cancel' | 'reject' | 'edit' | 'rewrite' | 'publish' | 'retry';

const ALLOWED: Record<ContentAction, readonly ContentState[]> = {
  write: ['idea'],
  cancel: ['queued'],
  reject: ['idea', 'queued', 'ready', 'failed'],
  edit: ['idea', 'ready'],
  rewrite: ['ready', 'failed'],
  publish: ['ready'],
  retry: ['failed'],
};

/** Whether a person may do this to an article in this state. */
export function contentActionAllowed(action: ContentAction, state: ContentState): boolean {
  return ALLOWED[action].includes(state);
}

/** A language tag as the markets use it: `ru`, `pt-BR`. */
const languageTag = z.string().trim().regex(/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/, 'A language tag such as en or pt-BR');

export const contentProfileSchema = z.object({
  /** The autopilot: topics and articles made to the weekly count without being asked. */
  enabled: z.boolean(),
  mode: z.enum(CONTENT_MODES),
  perWeek: z.number().int().min(1).max(21),
  /** What the site is about and for whom — what an editor would be told on their first day. */
  topic: z.string().trim().min(10).max(3000),
  tone: z.string().trim().max(500).default(''),
  keywords: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
  /** The length aimed at, in words (in characters ÷ 2 for Chinese, Japanese and Korean). */
  words: z.number().int().min(400).max(4000),
  /** Ground each article on a web search first; without it the model writes from what it knows. */
  research: z.boolean(),
  /** End an article with the sources it rests on. */
  sourcesList: z.boolean(),
  /** Overrides the site's language when the site has none. */
  language: languageTag.nullable(),
  /** The CMS login it publishes with: a WordPress application password in the site's credentials. */
  credentialId: z.string().uuid().nullable(),
  /** The CMS category new articles go to, by name; the model picks among the site's when empty. */
  category: z.string().trim().max(100).nullable(),
});
export type ContentProfileInput = z.infer<typeof contentProfileSchema>;

export const contentIdeaSchema = z.object({
  title: z.string().trim().min(5).max(200),
  keyword: z.string().trim().max(100).nullable().default(null),
  brief: z.string().trim().max(3000).nullable().default(null),
  /** Straight to the queue rather than into ideas. */
  write: z.boolean().default(false),
});

export const contentIdeasRequestSchema = z.object({ count: z.number().int().min(1).max(10).default(5) });

export const contentEditSchema = z.object({
  title: z.string().trim().min(5).max(200),
  keyword: z.string().trim().max(100).nullable().optional(),
  brief: z.string().trim().max(3000).nullable().optional(),
  metaDescription: z.string().trim().max(320).nullable().optional(),
  excerpt: z.string().trim().max(1000).nullable().optional(),
  html: z.string().max(200_000).nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(15).optional(),
  category: z.string().trim().max(100).nullable().optional(),
});

export const contentPublishSchema = z.object({ as: z.enum(['publish', 'draft']) });

// ------------------------------------------------------------------ cost

/** US dollars per million tokens, from Anthropic's price list; a fallback model is billed at its own. */
export const MODEL_PRICES: Record<string, { input: number; output: number; cacheWrite: number; cacheRead: number }> = {
  'claude-opus-5': { input: 5, output: 25, cacheWrite: 6.25, cacheRead: 0.5 },
  'claude-sonnet-5': { input: 2, output: 10, cacheWrite: 2.5, cacheRead: 0.2 },
  'claude-opus-4-8': { input: 5, output: 25, cacheWrite: 6.25, cacheRead: 0.5 },
};

/** Each web search the model runs, in US dollars ($10 per thousand). */
export const WEB_SEARCH_USD = 0.01;

export interface ModelUsage {
  readonly model: string;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheWriteTokens: number;
  readonly cacheReadTokens: number;
  readonly webSearches: number;
}

export function addUsage(a: ModelUsage | null, b: ModelUsage): ModelUsage {
  if (!a) return b;
  return {
    model: b.model,
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheWriteTokens: a.cacheWriteTokens + b.cacheWriteTokens,
    cacheReadTokens: a.cacheReadTokens + b.cacheReadTokens,
    webSearches: a.webSearches + b.webSearches,
  };
}

/** What a call cost, by the model that answered it; an unknown model at the dearest known price. */
export function usageCostUsd(usage: ModelUsage): number {
  const price = MODEL_PRICES[usage.model] ?? MODEL_PRICES['claude-opus-5']!;
  const tokens =
    usage.inputTokens * price.input + usage.outputTokens * price.output + usage.cacheWriteTokens * price.cacheWrite + usage.cacheReadTokens * price.cacheRead;
  return Math.round((tokens / 1_000_000 + usage.webSearches * WEB_SEARCH_USD) * 10_000) / 10_000;
}

/** The first moment of the calendar month, UTC: where a month's budget starts. */
export function monthStart(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

// ------------------------------------------------------------------ pace

const WEEK_MS = 7 * 24 * 60 * 60_000;

/** When the next article may go out: the week spread evenly, never before now. */
export function nextPublishAt(lastPublishedAt: Date | null, perWeek: number, now: Date): Date {
  if (!lastPublishedAt) return now;
  const next = new Date(lastPublishedAt.getTime() + Math.floor(WEEK_MS / Math.max(1, perWeek)));
  return next > now ? next : now;
}

/**
 * How many topics the autopilot adds to the queue now: the week's count less
 * what it made in the last seven days, and no more than keeps two on the way.
 */
export function autopilotShortfall(perWeek: number, madeLastWeek: number, onTheWay: number): number {
  return Math.max(0, Math.min(perWeek - madeLastWeek, 2 - onTheWay, 5));
}

// ------------------------------------------------------------------ html

const KEPT = new Set(['p', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'a', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'hr', 'code', 'pre', 'sup', 'sub', 'figure', 'figcaption']);
const DROPPED_WITH_CONTENT = /<(script|style|iframe|object|embed|svg|math|form|noscript|template|textarea|select|button)\b[\s\S]*?<\/\1\s*>/gi;
const VOID = new Set(['br', 'hr']);

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * An article's body made safe to publish: only the tags an article needs, no
 * attribute but a link's http(s) address and a table cell's span, no script
 * or frame with what is inside it. The model writes plain HTML already; this
 * is what holds when it does not.
 */
export function sanitizeArticleHtml(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(DROPPED_WITH_CONTENT, '')
    .replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (tag, rawName: string, attributes: string) => {
      const name = rawName.toLowerCase();
      if (!KEPT.has(name)) return '';
      if (tag.startsWith('</')) return VOID.has(name) ? '' : `</${name}>`;
      if (name === 'a') {
        const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attributes);
        const url = (href?.[1] ?? href?.[2] ?? href?.[3] ?? '').trim();
        return /^https?:\/\//i.test(url) ? `<a href="${escapeAttribute(url)}" rel="noopener">` : '<a>';
      }
      if (name === 'td' || name === 'th') {
        const spans = [...attributes.matchAll(/\b(colspan|rowspan)\s*=\s*["']?(\d{1,2})["']?/gi)].map((match) => ` ${match[1]!.toLowerCase()}="${match[2]}"`);
        return `<${name}${spans.join('')}>`;
      }
      return `<${name}>`;
    })
    .trim();
}

const escapeText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The list of sources an article ends with, under a heading in the article's language. */
export function sourcesHtml(heading: string, sources: readonly { readonly url: string; readonly title: string | null }[]): string {
  const items = sources
    .filter((source) => /^https?:\/\//i.test(source.url))
    .map((source) => `<li><a href="${escapeAttribute(source.url)}" rel="noopener" target="_blank">${escapeText(source.title?.trim() || new URL(source.url).hostname)}</a></li>`);
  return items.length === 0 ? '' : `<h2>${escapeText(heading.trim() || 'Sources')}</h2>\n<ul>\n${items.join('\n')}\n</ul>`;
}

/** An address part for the article: latin letters, digits and hyphens, or none — the CMS then makes its own from the title. */
export function articleSlug(value: string | null | undefined): string | null {
  const slug = (value ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70)
    .replace(/-+$/, '');
  return slug.length >= 3 ? slug : null;
}

/** An article's length as a reader counts it: words, or characters where words are not spaced. */
export function articleLength(html: string): { words: number; characters: number } {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/&[a-z#0-9]+;/gi, ' ').replace(/\s+/g, ' ').trim();
  const words = text ? text.split(' ').filter((word) => /[\p{L}\p{N}]/u.test(word)).length : 0;
  return { words, characters: text.replace(/\s/g, '').length };
}
